import download from "downloadjs";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import moment from "moment";
import _ from "lodash";
import { buildOrderWarrantyTexts } from "../../utils/buildOrderWarranties";
import { publicRequest } from "../../requestMethods";
import { formatAwningSupplementaryValue } from "../../constants/awningSupplementaryOptions";
import { store } from "../../redux/store";
import createIpohInvoice from "./createIpohInvoice";

import { isIpohAccountUser } from "../../utils/ipohAccount";
import { PAYMENT_SLOTS, paymentAmountField } from "../../constants/paymentSlots";

const shouldUseIpohInvoice = () =>
    isIpohAccountUser(store.getState()?.user?.currentUser);

const DEFAULT_INVOICE_HEADER = {
    name: "INHOME GROUP SDN BHD (202101002744 (1403042K))",
    addressLine1: "No 3A & 5, Jln Kpb 18, Kawasan Perindustrian Balakong,",
    addressLine2: "43100 Seri Kembangan, Selangor",
    phoneNumber: "+6 017 775 3383",
    email: "inhomegroup@gmail.com",
};

const PRINCIPLE_BUSINESS_DETAIL_ROWS = [
    { field: "businessRegNo", label: "Business Registration Number" },
    { field: "tin", label: "Business TIN" },
    { field: "salesTaxNo", label: "Business Sales Tax Number" },
    { field: "bankAccount", label: "Bank Account Number" },
];

const hasMeaningfulAddressPart = (v) => {
    if (v === undefined || v === null) return false;
    const s = String(v).trim();
    return s !== "" && s !== "-";
};

const formatPrincipleAddressLines = (address = {}) => {
    const line1 = String(address.lineOne || "").trim();
    const line2 = String(address.lineTwo || "").trim();
    const zip = String(address.zip || "").trim();
    const city = String(address.city || "").trim();

    let addressLine1 = DEFAULT_INVOICE_HEADER.addressLine1;
    if (hasMeaningfulAddressPart(line1) || hasMeaningfulAddressPart(line2)) {
        if (!hasMeaningfulAddressPart(line1)) {
            addressLine1 = line2;
        } else if (!hasMeaningfulAddressPart(line2)) {
            addressLine1 = line1.endsWith(",") ? line1 : `${line1},`;
        } else {
            addressLine1 = `${line1}, ${line2}`;
        }
    }

    const cityLineParts = [zip, city].filter((part) => hasMeaningfulAddressPart(part));
    const addressLine2 =
        cityLineParts.length > 0
            ? cityLineParts.join(" ")
            : DEFAULT_INVOICE_HEADER.addressLine2;

    return { addressLine1, addressLine2 };
};

const CONTACT_ICON_VIEWBOX = 24;
const CONTACT_ICON_PATHS = {
    phone:
        "M 3 5 A 2 2 0 0 1 5 3 H 8.28 A 1 1 0 0 1 9.23 3.684 L 10.73 8.177 A 1 1 0 0 1 10.23 9.387 L 7.97 10.517 A 11.042 11.042 0 0 0 13.49 16.033 L 14.62 13.776 A 1 1 0 0 1 15.83 13.276 L 20.32 14.774 A 1 1 0 0 1 21.27 15.723 V 19 A 2 2 0 0 1 19.27 21 H 18.27 C 10.28 21 3 13.72 3 5.72 V 5 Z",
    email:
        "M 3 8 L 10.89 13.26 A 2 2 0 0 0 13.11 13.26 L 21 8 M 5 19 H 19 A 2 2 0 0 0 21 17 V 7 A 2 2 0 0 0 19 5 H 5 A 2 2 0 0 0 3 7 V 17 A 2 2 0 0 0 5 19 Z",
    website:
        "M 12 3 A 9 9 0 0 1 12 21 A 9 9 0 0 1 12 3 M 21 12 A 9 9 0 0 0 3 12 M 21 12 H 3 M 12 3 C 13.66 3 15 7.03 15 12 C 15 16.97 13.66 21 12 21 M 12 3 C 10.34 3 9 7.03 9 12 C 9 16.97 10.34 21 12 21",
};

const drawContactIcon = (page, iconType, x, y, size) => {
    const path = CONTACT_ICON_PATHS[iconType];
    if (!path) return;

    const scale = size / CONTACT_ICON_VIEWBOX;
    page.drawSvgPath(path, {
        x,
        y: y - size * 0.85,
        scale,
        borderColor: rgb(0, 0, 0),
        borderWidth: 1.35,
    });
};

const drawContactInfoBlock = ({
    page,
    fontSize,
    valueFont,
    rows,
    yPositions,
    sanitizeText,
    rightEdge = 560,
    labelGap = 4,
    iconSize = 10,
    valueTextYOffset = 17,
}) => {
    const sanitizedRows = rows.map((row) => ({
        iconType: row.iconType,
        value: sanitizeText(row.value),
    }));

    const labelColumnWidth = iconSize + 1;
    const maxValueWidth = Math.max(
        ...sanitizedRows.map((row) =>
            valueFont.widthOfTextAtSize(row.value, fontSize)
        )
    );

    const blockWidth = labelColumnWidth + labelGap + maxValueWidth;
    const labelX = rightEdge - blockWidth;
    const valueX = labelX + labelColumnWidth + labelGap;

    sanitizedRows.forEach((row, index) => {
        const y = yPositions[index];
        drawContactIcon(page, row.iconType, labelX, y, iconSize);
        page.drawText(row.value, {
            x: valueX,
            y: y - valueTextYOffset,
            size: fontSize,
            font: valueFont,
            color: rgb(0, 0, 0),
        });
    });
};

// Commission is excluded so orders saved before commission was charged keep their stored amount
const ORDER_EXPENSE_KEYS = [
    "creditCard",
    "freeGive",
    "crane",
    "voucher",
    "drawing",
    "sponsor",
    "addon",
    "threedDrawing",
    "snSstCn",
    "swSstCn",
    "sstCn",
    "accessoryCosting",
];

const calculateOrderTotalExpenses = (expenses) => {
    if (!expenses || typeof expenses !== "object") return 0;
    return ORDER_EXPENSE_KEYS.reduce(
        (sum, key) => sum + (parseFloat(expenses[key] || 0) || 0),
        0,
    );
};

const resolveInvoiceTotalAmount = (order) => {
    let cartTotal = 0;
    if (Array.isArray(order?.products)) {
        for (const product of order.products) {
            cartTotal +=
                (parseFloat(product.price) || 0) *
                (parseFloat(product.amount) || 0);
        }
    }

    const expensesTotal = calculateOrderTotalExpenses(order?.expenses);
    const storedAmount = parseFloat(order?.amount || 0) || 0;
    const amountWithExpenses = cartTotal + expensesTotal;

    if (storedAmount > 0) {
        if (expensesTotal > 0 && storedAmount < amountWithExpenses - 0.01) {
            return amountWithExpenses;
        }
        return storedAmount;
    }

    return amountWithExpenses;
};

const resolvePrincipleHeader = async (order) => {
    let principle = order?.principle;
    const principleId =
        typeof principle === "string" || typeof principle === "number"
            ? principle
            : principle?._id;

    const needsFetch =
        !principle ||
        typeof principle === "string" ||
        typeof principle === "number" ||
        !principle?.name ||
        !principle?.phoneNumber ||
        !principle?.email;

    if (needsFetch && principleId) {
        try {
            const res = await publicRequest.get(`/users/find/${principleId}`);
            principle = res.data;
        } catch (err) {
            console.error("Failed to load principle account for invoice:", err);
        }
    }

    if (!principle || typeof principle !== "object") {
        return { ...DEFAULT_INVOICE_HEADER, detailLines: [] };
    }

    const { addressLine1, addressLine2 } = formatPrincipleAddressLines(
        principle.address || {}
    );

    return {
        name:
            String(principle.name || "").trim() || DEFAULT_INVOICE_HEADER.name,
        addressLine1,
        addressLine2,
        phoneNumber:
            String(principle.phoneNumber || "").trim() ||
            DEFAULT_INVOICE_HEADER.phoneNumber,
        email:
            String(principle.email || "").trim() || DEFAULT_INVOICE_HEADER.email,
        detailLines: PRINCIPLE_BUSINESS_DETAIL_ROWS.map(({ field, label }) => {
            const value = String(principle[field] || "").trim();
            return value ? `${label} : ${value}` : null;
        }).filter(Boolean),
    };
};

const DELIVERY_ORDER_ACK_RESERVE = 112;

const drawDeliveryOrderAcknowledgement = ({
    page,
    standardFontSize,
    timesRomanFont,
}) => {
    const leftX = 30;
    const leftW = 220;
    const rightX = 330;
    const rightW = 230;
    const detailSize = 8;
    const details = [
        "Name / Company Name",
        "NRIC / Company Registration No",
        "Date",
    ];
    const dateY = 32;
    const detailGap = 14.5;
    const captionY = dateY + details.length * detailGap + 4;
    const signLineY = captionY + 13;
    const centerInColumn = (text, colX, colW, textY) => {
        const width = timesRomanFont.widthOfTextAtSize(text, standardFontSize);
        page.drawText(text, {
            x: colX + Math.max((colW - width) / 2, 0),
            y: textY,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });
    };

    page.drawLine({
        start: { x: leftX, y: signLineY },
        end: { x: leftX + leftW, y: signLineY },
        thickness: 0.7,
        color: rgb(0, 0, 0),
    });
    page.drawLine({
        start: { x: rightX, y: signLineY },
        end: { x: rightX + rightW, y: signLineY },
        thickness: 0.7,
        color: rgb(0, 0, 0),
    });

    centerInColumn("Prepared By", leftX, leftW, captionY);
    centerInColumn("Received By", rightX, rightW, captionY);

    details.forEach((label, index) => {
        const textY = dateY + (details.length - 1 - index) * detailGap;
        page.drawText(label, {
            x: rightX,
            y: textY,
            size: detailSize,
            font: timesRomanFont,
            color: rgb(0.25, 0.25, 0.25),
        });
        const labelWidth = timesRomanFont.widthOfTextAtSize(label, detailSize);
        page.drawLine({
            start: { x: rightX + labelWidth + 6, y: textY - 1 },
            end: { x: rightX + rightW, y: textY - 1 },
            thickness: 0.45,
            color: rgb(0.35, 0.35, 0.35),
        });
    });
};

const createCustomerInvoice = async (original, options = {}) => {
    if (shouldUseIpohInvoice()) {
        return createIpohInvoice(original, options);
    }
    const { mergeLabourIntoLastItem = false, isDeliveryOrder = false } = options;
    try {
        const NumberFormatter = new Intl.NumberFormat("en-GB", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

        // Helper function to sanitize text for WinAnsi encoding
        const sanitizeText = (text) => {
            if (!text) return "";
            // Normalize typographic quotes/primes (e.g. feet/inches from mobile keyboards) to ASCII
            return String(text)
                .replace(/[\u2018\u2019\u2032\u02BC\u00B4\uFF07]/g, "'")
                .replace(/[\u201C\u201D\u2033\u2036]/g, '"')
                .replace(/\uFF08/g, "(")
                .replace(/\uFF09/g, ")")
                .replace(/[^\x20-\x7E\xA0-\xFF]/g, "")
                .replace(/\(\s*\)/g, "")
                .replace(/[ \t]{2,}/g, " ")
                .trim();
        };

        const capitalizeWords = (text) => {
            if (!text) return "";
            return String(text)
                .split(/\s+/)
                .filter(Boolean)
                .map(
                    (word) =>
                        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                )
                .join(" ");
        };

        // Standard font size for all text
        const standardFontSize = 9;
        
        // Mapping for awning item fields to human-readable labels
        const awningFieldLabels = {
            remoteControl: "Remote Control",
            rainingSensor: "Raining Sensor",
            birdVoiceSensor: "Bird Voice Sensor",
            ledLight: "LED Light",
            metalDeckModel: "Model",
            metaldeckColor: "Metal Deck Color",
            puMetalColor: "PU Metal Color",
            puFoamColor: "PU Foam Color",
            fiberglassColor: "Fiber Glass Color",
            aluminiumFlutedPanelColor: "Aluminium Fluted Panel Color",
            aluminiumFlutedPanelFoamColor: "Aluminium Fluted Panel with Foam Color",
            puMetalInfo: "PU Metal Info",
            size: "Size",
            area: "Area",
            frame: "Frame",
            steelPillar: "Steel Pillar",
            steelPillarQuantity: "Steel Pillar Qty",
            ceilingMaterial: "Ceiling Material",
            ceilingColor: "Ceiling Color",
            acpColor: "ACP Color",
            replacementPanel: "Replacement Panel",
            sideHoarding: "Side Hoarding",
            poleSupport: "Pole Support",
            poleSupportQuantity: "Pole Support Qty",
            aluminiumPillar: "Aluminium Pillar",
            aluminiumPillarQuantity: "Aluminium Pillar Qty",
            stainlessSteelPillar: "Stainless Steel Pillar",
            stainlessPillarQuantity: "Stainless Steel Pillar Qty",
            iBeamPillar: "I-Beam Pillar",
            ibeamPillarQuantity: "I-Beam Pillar Qty",
            mainSupport: "Main Support",
            mainSupportQuantity: "Main Support Qty",
            iBeamMainSupport: "I-Beam Main Support",
            ibeamMainSupportQuantity: "I-Beam Main Support Qty",
            ibeamMainSupportSize: "I-Beam Main Support Size (ft)",
            flashingCoverSize: "Flashing Cover Size",
            aluminiumLouversDegree: "Aluminium Louvers Degree",
            aluminiumLouversSize: "Aluminium Louvers Size",
            aluminiumLouversColor: "Aluminium Louvers Color",
            gutter: "Gutter",
            gutterSize: "Gutter Size",
            giCover: "G.I Cover",
            giCoverSize: "G.I Cover Size",
            uPerlinCover: "U-Purlin Cover",
            uPerlinCoverSize: "U-Purlin Cover Size",
            aluminiumCover: "Aluminium Cover",
            aluminiumCoverSize: "Aluminium Cover Size",
            aluminiumSingleTLightProfile: "Aluminium Single T-Light Profile",
            aluminiumSingleTLightSize: "Aluminium Single T-Light Size",
            aluminiumDoubleTLightProfile: "Aluminium Double T-Light Profile",
            aluminiumDoubleTLightSize: "Aluminium Double T-Light Size",
            dismantleRoofSize: "Dismantle Existing Roof Size",
            crane: "Crane",
            craneDays: "Crane Days",
            skylift: "Skylift",
            skyliftDays: "Skylift Days",
            glassColor10_38: "Laminated Safety Glass 10.38mm Color",
            glassColor12_38: "Laminated Safety Glass 12.38mm Color",
            glassColor10_76: "Laminated Safety IR Glass 10.76mm Color",
            glassColor12_76: "Laminated Safety IR Glass 12.76mm Color",
            lamiTempered11_52: "Laminated Tempered 11.52mm",
            lamiTempered11_52Quantity: "Laminated Tempered 11.52mm QTY",
            lamiTempered13_52: "Laminated Tempered 13.52mm",
            lamiTempered13_52Quantity: "Laminated Tempered 13.52mm QTY",
            inbondColor: "In-Bond Color",
            pmsColor: "PMS Color",
            cbondColor: "C-Bond Color",
            color3mm: "3mm Color",
            frameColor: "Frame Color",
            ceilingcapsize: "Ceiling Capping Size 2” ~ 8”",
            ceilingdropsize: "Ceiling Drop Size",
            rorobinSize: "Roro Bin Size",
            dismantleCeilingSize: "Dismantle Existing Plaster Ceiling Size",
            gDmlColor: "DML Color (G)",
            sDmlColor: "DML Color (S)",
            gSpaceColor: "Space Color (G)",
            sSpaceColor: "Space Color (S)",
        };

        const awningTypeLabels = {
            glass: "Glass Roof",
            aluminiumComposite: "Aluminium Composite Roof",
            aluminiumCBond: "Aluminium C-Bond",
            polyCarbonate: "Polycarbonate Roof",
            metalDeck: "Metal Deck Roof",
            aluminiumStrip: "Aluminium Strip Ceiling",
            retractableSystem: "Retractable System",
            others: "Others",
        };

        const awningSizeLabelByItemType = {
            glass: "Glass Size",
            aluminiumComposite: "Aluminium Composite Size",
            aluminiumCBond: "Aluminium C-Bond Size",
            polyCarbonate: "Polycarbonate Size",
            metalDeck: "Metal Deck Size",
            metalDeck2: "Metal Deck Size",
            aluminiumStrip: "Aluminium Strip Size",
            retractableSystem: "Retractable Size",
            retracableSystem: "Retractable Size",
            others: "Size",
        };

        const getAwningItemTypeKey = (item) => {
            const rawType = String(item?.type || "").trim();
            if (rawType) return rawType;
            const typeDescription = String(item?.typeDescription || "")
                .trim()
                .toLowerCase();
            if (typeDescription.includes("glass")) return "glass";
            if (typeDescription.includes("metal deck")) return "metalDeck";
            if (typeDescription.includes("polycarbonate")) return "polyCarbonate";
            if (
                typeDescription.includes("c-bond") ||
                typeDescription.includes("cbond")
            ) {
                return "aluminiumCBond";
            }
            if (typeDescription.includes("aluminium composite")) {
                return "aluminiumComposite";
            }
            if (
                typeDescription.includes("aluminium strip") ||
                typeDescription.includes("strip ceiling")
            ) {
                return "aluminiumStrip";
            }
            if (typeDescription.includes("retractable")) {
                return "retractableSystem";
            }
            return "";
        };

        const getAwningFieldLabel = (item, field) => {
            if (field === "size") {
                const itemType = getAwningItemTypeKey(item);
                return awningSizeLabelByItemType[itemType] || "Size";
            }
            return awningFieldLabels[field] || field;
        };

        /** Main invoice line title for awning catalog products (from items[0].type). */
        const awningMainTitleByItemType = {
            aluminiumComposite: "Aluminium Composite Panel C/W Metal Frame",
            aluminiumCBond: "Aluminium C-Bond",
            glass: "Safety Laminated Glass C/W Metal Frame",
            polyCarbonate: "Polycarbonate Sheet C/W Metal Frame",
            metalDeck: "Metal Deck PU Foam C/W Metal Frame",
            metalDeck2: "Metal Deck PU Foam C/W Metal Frame",
            retractableSystem: "Retracable System",
            retracableSystem: "Retracable System",
            aluminiumStrip: "Aluminium Strip Ceiling",
        };

        const getFirstProductItemType = (productRow) => {
            if (Array.isArray(productRow.items) && productRow.items.length > 0) {
                return productRow.items[0].type;
            }
            if (
                Array.isArray(productRow.productItems) &&
                productRow.productItems.length > 0
            ) {
                return productRow.productItems[0].type;
            }
            return productRow.type;
        };

        const getAwningMainTitle = (productRow) => {
            const itemType = getFirstProductItemType(productRow);
            if (itemType && awningMainTitleByItemType[itemType]) {
                return awningMainTitleByItemType[itemType];
            }
            return null;
        };

        /** Full Awning POS catalog product; other productIds use order `code` on invoice instead of generic "Awning" */
        const MAIN_AWNING_CATALOG_PRODUCT_ID =
            "69832bf1412f46e3c0c8dec7";

        const getOrderProductIdString = (pr) => {
            const pid = pr?.productId;
            if (pid == null) return "";
            if (typeof pid === "object") {
                const id = pid._id ?? pid.id;
                return id != null ? String(id) : "";
            }
            return String(pid);
        };

        const isMainAwningCatalogProduct = (pr) =>
            getOrderProductIdString(pr) === MAIN_AWNING_CATALOG_PRODUCT_ID;

        // Group rows by each product entry:
        // all items inside one product share the same invoice number/description.
        const buildProductRows = () =>
            original.products.map((product) => {
                const productItems =
                    Array.isArray(product.items) && product.items.length > 0
                        ? product.items.map((item) => ({
                              ...item,
                              isNonAwning: false,
                          }))
                        : [
                              {
                                  type: product.type,
                                  typeDescription: product.typeDescription,
                                  itemCode: product.itemCode,
                                  frameColor: product.frameColor,
                                  size: product.size,
                                  model: product.model,
                                  isNonAwning: true,
                              },
                          ];

                return {
                    ...product,
                    productItems,
                };
            });

        const getRowAreaKey = (row) => {
            if (row.productItems.some((item) => item.isNonAwning)) return "";
            const areas = row.productItems.map((item) =>
                String(item.area || "").trim().toLowerCase()
            );
            if (!areas[0] || areas.some((area) => area !== areas[0])) return "";
            return areas[0];
        };

        // Rows sharing the same area are listed under one invoice number
        const mergeProductRowsByArea = (rows) => {
            const merged = [];
            const rowsByArea = new Map();
            rows.forEach((row) => {
                const areaKey = getRowAreaKey(row);
                const taggedItems = row.productItems.map((item) => ({
                    ...item,
                    sourceRow: row,
                }));
                if (areaKey && rowsByArea.has(areaKey)) {
                    const target = rowsByArea.get(areaKey);
                    target.sourceRows.push(row);
                    target.productItems.push(...taggedItems);
                    return;
                }
                const entry = {
                    ...row,
                    productItems: taggedItems,
                    sourceRows: [row],
                    mergedArea: areaKey ? row.productItems[0].area : "",
                };
                if (areaKey) rowsByArea.set(areaKey, entry);
                merged.push(entry);
            });
            return merged;
        };

        const productRows = mergeProductRowsByArea(buildProductRows());

        const COMBINED_ITEMS_PRINCIPLE_ID = "686fc107e6d68e77cd2512f7";
        const getOrderPrincipleId = (order) => {
            const principle = order?.principle;
            if (principle == null) return "";
            if (typeof principle === "object") {
                const id = principle._id ?? principle.id;
                return id != null ? String(id) : "";
            }
            return String(principle);
        };
        const useCombinedProductItemsLayout =
            getOrderPrincipleId(original) === COMBINED_ITEMS_PRINCIPLE_ID;

        // Order line is "Others" (POS / manual): show product.code on invoice, not generic "Awning"
        const isOthersOrderLine = (pr) => {
            const ic = String(pr?.itemCode || "").toUpperCase();
            if (ic === "OTHERS") return true;
            if (String(pr?.type || "").toLowerCase() === "others") return true;
            if (String(pr?.typeDescription || "").toLowerCase() === "others")
                return true;
            const pid = pr?.productId;
            if (pid && typeof pid === "object") {
                if (String(pid.title || "").toLowerCase() === "others")
                    return true;
            }
            if (Array.isArray(pr?.items)) {
                return pr.items.some((it) => {
                    const typ = String(it?.type || "").toLowerCase();
                    const td = String(it?.typeDescription || "").toLowerCase();
                    return typ === "others" || td === "others";
                });
            }
            return false;
        };

        /** Prefer order `code` for invoice when it is not the fixed OTHERS sentinel */
        const othersInvoicePrimaryText = (pr) => {
            const raw = pr?.code;
            if (raw === undefined || raw === null) return "";
            const s = String(raw).trim();
            if (!s || s.toUpperCase() === "OTHERS") return "";
            return s;
        };

        const hasMeaningfulAddressPart = (v) => {
            if (v === undefined || v === null) return false;
            const s = String(v).trim();
            return s !== "" && s !== "-";
        };
        const pickInvoiceAddress = (billing, service) =>
            hasMeaningfulAddressPart(billing) ? billing : service ?? "";
        const invoiceAddrLine1 = pickInvoiceAddress(
            original.billingLineOne,
            original.lineOne
        );
        const invoiceAddrLine2 = pickInvoiceAddress(
            original.billingLineTwo,
            original.lineTwo
        );
        const invoiceZip = pickInvoiceAddress(original.billingZip, original.zip);
        const invoiceCity = pickInvoiceAddress(
            original.billingCity,
            original.city
        );
        const invoiceCountry = pickInvoiceAddress(
            original.billingCountry,
            original.country
        );
        const invoiceAddressFirstLine = (() => {
            const line1 = String(invoiceAddrLine1 || "").trim();
            const line2 = String(invoiceAddrLine2 || "").trim();
            if (!hasMeaningfulAddressPart(line1) && !hasMeaningfulAddressPart(line2)) {
                return "";
            }
            if (!hasMeaningfulAddressPart(line1)) return line2;
            if (!hasMeaningfulAddressPart(line2)) return `${line1},`;
            return `${line1}, ${line2}`;
        })();

        // Create a new PDFDocument
        const pdfDoc = await PDFDocument.create();

        // Embed the Times Roman font
        const timesRomanFont = await pdfDoc.embedFont(StandardFonts.TimesRoman);
        const timesRomanBoldFont = await pdfDoc.embedFont(
            StandardFonts.TimesRomanBold
        );

        // Use order amount (cart + expenses) as invoice total
        const totalAmount = resolveInvoiceTotalAmount(original);

        const fontSize = 20;
        const bottomMargin = 40; // Minimum space to leave at bottom before creating new page
        const tableLineStep = 0.5;
        const tableRowGap = 0.4;
        const subItemHeaderGap = tableLineStep + tableRowGap;
        const qtyColLeft = 508;
        const qtyColRight = 545;
        const getQtyX = (text) => {
            if (!isDeliveryOrder) return 320;
            const width = timesRomanFont.widthOfTextAtSize(
                String(text),
                standardFontSize
            );
            return qtyColLeft + (qtyColRight - qtyColLeft - width) / 2;
        };
        const colUnitPrice = 368;
        const colSst = 442;
        const colAmount = 498;
        const getAmountTextX = (text) =>
            colAmount +
            timesRomanFont.widthOfTextAtSize("Amount (RM)", standardFontSize) -
            timesRomanFont.widthOfTextAtSize(text, standardFontSize);
        const lineTitleFontSize = 10;
        const lineTitleDetailGap = 0.15;
        
        // Helper to get item details
        const getItemDetails = (item, { skipArea = false } = {}) => {
            const details = [];

            if (item.isNonAwning) {
                if (item.itemCode) details.push(`Item Code: ${item.itemCode}`);
                if (item.type) details.push(`Type: ${item.type}`);
                if (item.frameColor) details.push(`Frame Colour: ${item.frameColor}`);
                if (item.size) details.push(`Size: ${item.size}`);
                if (item.model) details.push(`Model: ${item.model}`);
            } else {
                const pushField = (field, label) => {
                    const value = item[field];
                    if (
                        value === undefined ||
                        value === null ||
                        value === "" ||
                        value === false
                    ) {
                        return;
                    }
                    const displayValue =
                        typeof value === "boolean"
                            ? "Yes"
                            : field === "area"
                              ? capitalizeWords(String(value))
                              : formatAwningSupplementaryValue(field, value);
                    details.push(`${label}: ${displayValue}`);
                };
                // Area directly under the main title line
                if (!skipArea) {
                    pushField("area", getAwningFieldLabel(item, "area"));
                }
                Object.entries(awningFieldLabels).forEach(([field]) => {
                    if (field === "area") return;
                    pushField(field, getAwningFieldLabel(item, field));
                });
            }

            return details;
        };

        const getItemTypeLabel = (item, productRow) => {
            if (item.isNonAwning) {
                const isOthersNonAwning =
                    String(item.type || "").toLowerCase() === "others" ||
                    String(item.typeDescription || "").toLowerCase() ===
                        "others" ||
                    String(item.itemCode || "").toUpperCase() === "OTHERS";
                if (isOthersNonAwning) {
                    return (
                        othersInvoicePrimaryText(productRow) ||
                        item.typeDescription ||
                        item.type ||
                        "Item"
                    );
                }
                if (!isMainAwningCatalogProduct(productRow)) {
                    const codeStr = String(productRow.code ?? "").trim();
                    return (
                        codeStr ||
                        item.typeDescription ||
                        item.type ||
                        "Item"
                    );
                }
                return item.typeDescription || item.type || "Item";
            }
            if (String(item.type || "").toLowerCase() === "others") {
                return (
                    othersInvoicePrimaryText(productRow) ||
                    item.typeDescription ||
                    productRow.code ||
                    awningTypeLabels.others
                );
            }
            return (
                awningTypeLabels[item.type] ||
                item.typeDescription ||
                item.type ||
                "Item"
            );
        };

        const getProductRowDetails = (productRow) => {
            const details = [];
            const hasMultipleItems = productRow.productItems.length > 1;
            const isAreaMerged = (productRow.sourceRows?.length || 0) > 1;
            const itemDetailOptions = { skipArea: isAreaMerged };

            if (isAreaMerged) {
                details.push(
                    `${awningFieldLabels.area}: ${capitalizeWords(String(productRow.mergedArea))}`
                );
            }

            if (useCombinedProductItemsLayout && hasMultipleItems) {
                const combinedTitle = productRow.productItems
                    .map((item) => getItemTypeLabel(item, item.sourceRow || productRow))
                    .filter(Boolean)
                    .join(" + ");
                if (combinedTitle) {
                    details.push(combinedTitle);
                }
                productRow.productItems.forEach((item) => {
                    getItemDetails(item, itemDetailOptions).forEach((detail) => {
                        details.push(detail);
                    });
                });
                return details;
            }

            productRow.productItems.forEach((item) => {
                const itemTypeLabel = getItemTypeLabel(item, item.sourceRow || productRow);

                if (hasMultipleItems) {
                    details.push(`- ${itemTypeLabel}`);
                }

                const subDetails = getItemDetails(item, itemDetailOptions);
                subDetails.forEach((detail) => {
                    details.push(hasMultipleItems ? `  ${detail}` : detail);
                });
            });

            return details;
        };

        // Create first page with header
        let currentPage = pdfDoc.addPage();
        let pageHeight = currentPage.getSize().height;
        const defaultPageWidth = currentPage.getSize().width;
        const defaultPageHeight = pageHeight;
        let currentY = 2; // Start position (will be updated after header)
        let isFirstPage = true;
        let currentItemNumber = 0;
        let hasMultiplePages = false;

        // Function to draw table headers
        const drawTableHeaders = (page, yPosition) => {
            page.drawLine({
                start: { x: 30, y: pageHeight - (yPosition - 0.5) * fontSize },
                end: { x: 560, y: pageHeight - (yPosition - 0.5) * fontSize },
                color: rgb(0, 0, 0),
                opacity: 0.75,
            });

            page.drawText("No", {
                x: 40,
                y: pageHeight - yPosition * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            page.drawText("Description", {
                x: 80,
                y: pageHeight - yPosition * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            page.drawText("Qty", {
                x: getQtyX("Qty"),
                y: pageHeight - yPosition * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            if (!isDeliveryOrder) {
                page.drawText("U/ Price (RM)", {
                    x: colUnitPrice,
                    y: pageHeight - yPosition * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });

                page.drawText("SST (5%)", {
                    x: colSst,
                    y: pageHeight - yPosition * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });

                page.drawText("Amount (RM)", {
                    x: colAmount,
                    y: pageHeight - yPosition * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });
            }

            page.drawLine({
                start: { x: 30, y: pageHeight - (yPosition + 0.4) * fontSize },
                end: { x: 560, y: pageHeight - (yPosition + 0.4) * fontSize },
                color: rgb(0, 0, 0),
                opacity: 0.75,
            });
        };

        // Function to create a new page
        const createNewPage = () => {
            currentPage = pdfDoc.addPage();
            pageHeight = currentPage.getSize().height;
            currentY = 2; // Start near top
            isFirstPage = false;
            hasMultiplePages = true;
            
            // Draw table headers on new page
            drawTableHeaders(currentPage, currentY);
            currentY = 3.5; // Position after headers
        };

        // Function to check if we need a new page
        const needsNewPage = (linesNeeded = 1) => {
            const remainingY = pageHeight - (currentY + linesNeeded) * fontSize;
            const minBottom = isDeliveryOrder
                ? DELIVERY_ORDER_ACK_RESERVE
                : bottomMargin;
            return remainingY < minBottom;
        };

        // Render header on first page
        const principleHeader = await resolvePrincipleHeader(original);
        const logoUrl =
            "https://ik.imagekit.io/dnddecpho/IHGroup/inhomelogo-new_80yctapLk.png";
        const logoBytes = await fetch(logoUrl).then((res) => res.arrayBuffer());
        const logoImage = await pdfDoc.embedPng(logoBytes);
        // Visible artwork inside the transparent file, so it occupies the same
        // left-hand box as the previous header logo (75 x 56 at x = 20).
        const logoArt = { left: 308, top: 92, right: 1142, bottom: 698 };
        const logoScale = 75 / (logoArt.right - logoArt.left);
        currentPage.drawImage(logoImage, {
            x: 20 - logoArt.left * logoScale,
            y:
                pageHeight -
                4 * fontSize -
                (logoImage.height - logoArt.bottom) * logoScale,
            width: logoImage.width * logoScale,
            height: logoImage.height * logoScale,
        });

        currentPage.drawText(sanitizeText(principleHeader.name), {
            x: 130,
            y: pageHeight - 1.9 * fontSize,
            size: standardFontSize,
            font: timesRomanBoldFont,
            color: rgb(0, 0, 0),
        });

        [
            principleHeader.addressLine1,
            principleHeader.addressLine2,
            ...principleHeader.detailLines,
        ].forEach((line, index) => {
            currentPage.drawText(sanitizeText(line), {
                x: 130,
                y: pageHeight - (2.5 + index * 0.55) * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });
        });

        drawContactInfoBlock({
            page: currentPage,
            fontSize: standardFontSize,
            valueFont: timesRomanFont,
            sanitizeText,
            rows: [
                { iconType: "phone", value: principleHeader.phoneNumber },
                { iconType: "email", value: principleHeader.email },
                { iconType: "website", value: "www.inhome.com.my" },
            ],
            yPositions: [
                pageHeight - 4.1 * fontSize,
                pageHeight - 4.8 * fontSize,
                pageHeight - 5.5 * fontSize,
            ],
        });

        const documentTitle = isDeliveryOrder
            ? "DELIVERY ORDER"
            : original.paymentStatus === "completed"
              ? "INVOICE"
              : "SALES ORDER";
        const documentTitleSize = 15;
        const documentTitleTracking = 3;
        const documentTitleColor = rgb(0.15, 0.15, 0.15);
        const documentTitleRuleColor = rgb(0.7, 0.7, 0.7);
        const documentTitleY = pageHeight - 7.45 * fontSize;
        const documentTitleChars = documentTitle.split("");
        const documentTitleWidth =
            documentTitleChars.reduce(
                (width, char) =>
                    width + timesRomanBoldFont.widthOfTextAtSize(char, documentTitleSize),
                0
            ) +
            documentTitleTracking * (documentTitleChars.length - 1);
        const documentTitleStartX = 295 - documentTitleWidth / 2;
        let documentTitleX = documentTitleStartX;
        documentTitleChars.forEach((char) => {
            currentPage.drawText(char, {
                x: documentTitleX,
                y: documentTitleY,
                size: documentTitleSize,
                font: timesRomanBoldFont,
                color: documentTitleColor,
            });
            documentTitleX +=
                timesRomanBoldFont.widthOfTextAtSize(char, documentTitleSize) +
                documentTitleTracking;
        });
        const documentTitleRuleY = documentTitleY + documentTitleSize * 0.33;
        [
            [30, documentTitleStartX - 14],
            [documentTitleStartX + documentTitleWidth + 14, 560],
        ].forEach(([startX, endX]) => {
            currentPage.drawLine({
                start: { x: startX, y: documentTitleRuleY },
                end: { x: endX, y: documentTitleRuleY },
                thickness: 0.75,
                color: documentTitleRuleColor,
            });
        });

        const orderInfoRows = [
            ["Invoice ID:", _.padStart(original.invoiceId, 4, "0")],
            [
                isDeliveryOrder ? "Delivery Order ID:" : "Sales Order ID:",
                isDeliveryOrder
                    ? original.deliveryOrderNo || "-"
                    : original.salesOrderId || "-",
            ],
            ["CO Number:", original.coNumber],
            ["Date of Issue:", moment(original.createdAt).format("DD MMM YYYY")],
            ["Accounts Name:", capitalizeWords(original?.userId?.name)],
            ["Phone Number:", original?.userId?.phoneNumber],
            ["Email:", original?.userId?.email],
            ["Address:", invoiceAddressFirstLine],
            ["", `${invoiceZip}, ${invoiceCity}, ${invoiceCountry}`],
        ];
        orderInfoRows.forEach(([label, value], index) => {
            const rowY = pageHeight - (8.6 + index * 0.7) * fontSize;
            if (label) {
                currentPage.drawText(label, {
                    x: 25,
                    y: rowY,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });
            }
            currentPage.drawText(sanitizeText(value), {
                x: 140,
                y: rowY,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });
        });
        
        currentPage.drawLine({
            start: {
                x: 30,
                y: pageHeight - 15 * fontSize,
            },
            end: {
                x: 560,
                y: pageHeight - 15 * fontSize,
            },
            color: rgb(0, 0, 0),
            opacity: 0.75,
        });

        currentPage.drawText("No", {
            x: 40,
            y: pageHeight - 15.6 * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });

        currentPage.drawText("Description", {
            x: 80,
            y: pageHeight - 15.6 * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });

        currentPage.drawText("Qty", {
            x: getQtyX("Qty"),
            y: pageHeight - 15.6 * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });

        if (!isDeliveryOrder) {
            currentPage.drawText("U/ Price (RM)", {
                x: colUnitPrice,
                y: pageHeight - 15.6 * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            currentPage.drawText("SST (5%)", {
                x: colSst,
                y: pageHeight - 15.6 * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            currentPage.drawText("Amount (RM)", {
                x: colAmount,
                y: pageHeight - 15.6 * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });
        }

        currentPage.drawLine({
            start: {
                x: 30,
                y: pageHeight - 16 * fontSize,
            },
            end: {
                x: 560,
                y: pageHeight - 16 * fontSize,
            },
            color: rgb(0, 0, 0),
            opacity: 0.75,
        });

        // Set starting Y position after header
        currentY = 17.5;

        const sstIncluded = original.sstIncluded === true;
        const roundMoney = (value) =>
            Math.round((value + Number.EPSILON) * 100) / 100;

        const productTotal = totalAmount * 0.295566;
        let productUnitPrice;
        let productSst;
        let productAmountIncl;
        let labourCharge;

        if (sstIncluded) {
            productUnitPrice = roundMoney(productTotal);
            productSst = roundMoney(productUnitPrice * 0.05);
            productAmountIncl = roundMoney(productUnitPrice * 1.05);
            labourCharge = roundMoney(totalAmount - productAmountIncl);
        } else {
            productUnitPrice = null;
            productSst = 0;
            productAmountIncl = null;
            labourCharge = roundMoney(totalAmount);
        }

        // Render one invoice row per product with grouped details
        for (let itemIndex = 0; itemIndex < productRows.length; itemIndex++) {
            const productRow = productRows[itemIndex];
            const rowDetails = getProductRowDetails(productRow);
            const isLastItem = itemIndex === productRows.length - 1;
            
            currentItemNumber = itemIndex + 1;

            // For items after the first, try to keep item header together with at least some details
            // For the first item, always start on page 1 (don't jump to new page immediately)
            if (itemIndex > 0) {
                // Keep at least the header + 3 detail lines together (or all if fewer)
                const minLinesToKeepTogether = Math.min(5, 2 + rowDetails.length);
                if (needsNewPage(minLinesToKeepTogether)) {
                    createNewPage();
                }
            }
            
            // Check if we need new page just for the header line
            if (needsNewPage(1)) {
                createNewPage();
            }
            
            // Draw item number
            currentPage.drawText(`${currentItemNumber}`, {
                x: 40,
                y: pageHeight - currentY * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
                weight: 600,
            });

            // Main item description — Others lines use order `code` (product.code), not "Awning"
            const getRowMainDescription = (row) => {
                const customTitle = String(row.title ?? "").trim();
                if (customTitle) return customTitle;
                if (isOthersOrderLine(row)) {
                    return (
                        othersInvoicePrimaryText(row) ||
                        row.typeDescription ||
                        row.type ||
                        awningTypeLabels.others
                    );
                }
                if (isMainAwningCatalogProduct(row)) {
                    return (
                        getAwningMainTitle(row) ||
                        row.typeDescription ||
                        awningTypeLabels[row.type] ||
                        row.type ||
                        "Awning"
                    );
                }
                const codeStr = String(row.code ?? "").trim();
                return (
                    codeStr ||
                    row.typeDescription ||
                    awningTypeLabels[row.type] ||
                    row.type ||
                    "Item"
                );
            };
            const fullDescription = [
                ...new Set(
                    (productRow.sourceRows || [productRow]).map(getRowMainDescription)
                ),
            ].join(" + ");
            const nameParts = fullDescription
                .split(/\s+\+\s+/)
                .map((part) => sanitizeText(part))
                .filter(Boolean);
            const mainDescription =
                nameParts.length > 1 ? nameParts[0] : nameParts[0] || sanitizeText(fullDescription);
            const addOnDetails =
                nameParts.length > 1
                    ? [`AddOn: ${nameParts.slice(1).join(" + ")}`]
                    : [];
            const isAreaMergedRow = (productRow.sourceRows?.length || 0) > 1;
            currentPage.drawText(sanitizeText(mainDescription), {
                x: 80,
                y: pageHeight - currentY * fontSize,
                size: lineTitleFontSize,
                font: timesRomanBoldFont,
                color: rgb(0, 0, 0),
            });

            // Quantity on the same line as main description
            const rowQuantity = isAreaMergedRow
                ? 1
                : productRow.quantity || productRow.amount || 1;
            currentPage.drawText(`${rowQuantity}`, {
                x: getQtyX(`${rowQuantity}`),
                y: pageHeight - currentY * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            // Only show total price for the last item when SST is included,
            // or when labour is merged into the last item without a separate line.
            if (!isDeliveryOrder && isLastItem && sstIncluded) {
                const displayUnitPrice = mergeLabourIntoLastItem
                    ? roundMoney(productUnitPrice + labourCharge)
                    : productUnitPrice;
                const displayAmount = mergeLabourIntoLastItem
                    ? roundMoney(productAmountIncl + labourCharge)
                    : productAmountIncl;

                currentPage.drawText(
                    `${NumberFormatter.format(displayUnitPrice)}`,
                    {
                        x: colUnitPrice,
                        y: pageHeight - currentY * fontSize,
                        size: standardFontSize,
                        font: timesRomanFont,
                        color: rgb(0, 0, 0),
                    }
                );

                currentPage.drawText(
                    `${NumberFormatter.format(productSst)}`,
                    {
                        x: colSst,
                        y: pageHeight - currentY * fontSize,
                        size: standardFontSize,
                        font: timesRomanFont,
                        color: rgb(0, 0, 0),
                    }
                );

                currentPage.drawText(
                    `${NumberFormatter.format(displayAmount)}`,
                    {
                        x: getAmountTextX(NumberFormatter.format(displayAmount)),
                        y: pageHeight - currentY * fontSize,
                        size: standardFontSize,
                        font: timesRomanFont,
                        color: rgb(0, 0, 0),
                    }
                );
            } else if (!isDeliveryOrder && isLastItem && mergeLabourIntoLastItem && !sstIncluded) {
                currentPage.drawText(
                    `${NumberFormatter.format(labourCharge)}`,
                    {
                        x: colUnitPrice,
                        y: pageHeight - currentY * fontSize,
                        size: standardFontSize,
                        font: timesRomanFont,
                        color: rgb(0, 0, 0),
                    }
                );

                currentPage.drawText(
                    `${NumberFormatter.format(labourCharge)}`,
                    {
                        x: getAmountTextX(NumberFormatter.format(labourCharge)),
                        y: pageHeight - currentY * fontSize,
                        size: standardFontSize,
                        font: timesRomanFont,
                        color: rgb(0, 0, 0),
                    }
                );
            }

            currentY += tableLineStep; // Move to next line for details
            const detailsToDraw = [...addOnDetails, ...rowDetails];
            if (detailsToDraw.length > 0) {
                currentY += lineTitleDetailGap;
            }

            // Render grouped row details, creating new pages as needed
            for (let d = 0; d < detailsToDraw.length; d++) {
                const detail = detailsToDraw[d];
                const addSubItemGap = d > 0 && detail.startsWith("- ");
                const linesNeeded = addSubItemGap
                    ? 1 + subItemHeaderGap / tableLineStep
                    : 1;

                // Check if we need a new page
                if (needsNewPage(linesNeeded)) {
                    createNewPage();
                }

                if (addSubItemGap) {
                    currentY += subItemHeaderGap;
                }

                currentPage.drawText(sanitizeText(detail), {
                    x: 80,
                    y: pageHeight - currentY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });
                currentY += tableLineStep;
            }

            currentY += tableLineStep; // Blank line after item details
            currentY += tableRowGap; // Spacing between grouped product rows
        }

        const includedExpenseLines = [
            ["threedDrawing", "3D Drawing"],
            ["crane", "Crane"],
        ]
            .filter(([key]) => (parseFloat(original.expenses?.[key]) || 0) > 0)
            .map(([, label]) => label);

        // Add Labour/Installation Charge and footer
        const footerLinesNeeded =
            (mergeLabourIntoLastItem ? 8 : 12) + includedExpenseLines.length;
        if (needsNewPage(footerLinesNeeded)) {
            createNewPage();
        }

        if (!mergeLabourIntoLastItem) {
        // Always calculate labour charge as remainder after product amount incl. SST
        // Labour charge comes after all items
        const labourChargeNumber = productRows.length + 1;
            
        // Product number
        currentPage.drawText(`${labourChargeNumber}`, {
            x: 40,
            y: pageHeight - currentY * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
            weight: 600,
        });

        // Labour/Installation Charge description
        currentPage.drawText("Labour / Installation Charge", {
            x: 80,
            y: pageHeight - currentY * fontSize,
            size: lineTitleFontSize,
            font: timesRomanBoldFont,
            color: rgb(0, 0, 0),
        });

        // Quantity
        currentPage.drawText("1", {
            x: getQtyX("1"),
            y: pageHeight - currentY * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });

        // Unit price only — no SST for labour / installation charge
        currentPage.drawText(
            `${NumberFormatter.format(labourCharge)}`,
            {
                x: colUnitPrice,
                y: pageHeight - currentY * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            }
        );

        currentPage.drawText(
            `${NumberFormatter.format(labourCharge)}`,
            {
                x: getAmountTextX(NumberFormatter.format(labourCharge)),
                y: pageHeight - currentY * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            }
        );

        currentY += 0.6; // Spacing after labour charge
        }
        
        // Add warranty information after labour charge
        if (!isDeliveryOrder) {
        currentY += 0.6; // Spacing before warranty section

        if (includedExpenseLines.length > 0) {
            includedExpenseLines.forEach((label) => {
                currentPage.drawText(label, {
                    x: 80,
                    y: pageHeight - currentY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });
                currentY += 0.5;
            });
            currentY += 0.3;
        }

        const drawWarrantySectionLines = (lines) => {
            lines.forEach((line) => {
                if (needsNewPage(1)) {
                    createNewPage();
                }
                if (line.trim()) {
                    currentPage.drawText(sanitizeText(line), {
                        x: 80,
                        y: pageHeight - currentY * fontSize,
                        size: standardFontSize,
                        font: timesRomanFont,
                        color: rgb(0, 0, 0),
                    });
                }
                currentY += 0.5;
            });
        };

        const extraWarrantyLines = String(original.extraWarranty || "")
            .replace(/\t/g, "    ")
            .split(/\r\n|\r|\n/);
        while (extraWarrantyLines.length && !extraWarrantyLines[0].trim()) {
            extraWarrantyLines.shift();
        }
        while (
            extraWarrantyLines.length &&
            !extraWarrantyLines[extraWarrantyLines.length - 1].trim()
        ) {
            extraWarrantyLines.pop();
        }
        const warrantyItems = buildOrderWarrantyTexts(original);

        drawWarrantySectionLines(extraWarrantyLines);
        if (extraWarrantyLines.length > 0 && warrantyItems.length > 0) {
            currentY += 0.3;
        }
        drawWarrantySectionLines(warrantyItems);
        
        currentY += 0.6; // Spacing after warranty section
        }

        if (isDeliveryOrder) {
            if (needsNewPage(0)) {
                createNewPage();
            }
            drawDeliveryOrderAcknowledgement({
                page: currentPage,
                standardFontSize,
                timesRomanFont,
            });
        } else {

        const currencySymbol =
            process.env.REACT_APP_COUNTRY === "SINGAPORE" ? "$" : "RM";
        const summaryLabelX = 370;
        const summaryCurrencyX = 470;
        const summaryRowStep = 0.7;
        const toAmount = (value) => {
            const n = Number(value);
            return Number.isFinite(n) ? n : 0;
        };
        const isMeaningfulAmount = (value) => {
            if (value === undefined || value === null) return false;
            if (typeof value === "string" && ["", "-"].includes(value.trim())) {
                return false;
            }
            return Number.isFinite(Number(value));
        };
        const firstMeaningfulAmount = (...values) => {
            const value = values.find(isMeaningfulAmount);
            return value === undefined ? null : Number(value);
        };

        const isInstallmentInvoice = original.paymentStatus === "installment";
        const discountAmount = toAmount(original.discount);
        const tradeInAmount = original.credit ? toAmount(original.credit.credit) : 0;
        const grandTotal = totalAmount - discountAmount - tradeInAmount;
        const depositAmount = toAmount(original.deposit);

        const summaryRows = [
            {
                label: "SUB TOTAL:",
                amount: isInstallmentInvoice ? depositAmount : totalAmount,
            },
        ];
        if (discountAmount !== 0) {
            summaryRows.push({ label: "DISCOUNT:", amount: discountAmount });
        }
        if (original.credit) {
            summaryRows.push({ label: "TRADE IN:", amount: tradeInAmount });
        }
        summaryRows.push({
            label: "TOTAL:",
            amount: isInstallmentInvoice ? depositAmount : grandTotal,
            gapBefore: 0.35,
        });

        // Only show payment details if paymentStatus is not "installment" and not (draft with deposit 0)
        if (
            !isInstallmentInvoice &&
            !(original.status === "draft" && original.deposit === 0)
        ) {
            const isCompleted = original.paymentStatus === "completed";
            const paymentRows = PAYMENT_SLOTS.map((slot, index) => ({
                label: slot.invoiceLabel,
                amount:
                    index === 0
                        ? original.firstPayment
                            ? Number(original.firstPayment)
                            : null
                        : firstMeaningfulAmount(
                              original[paymentAmountField(slot)],
                              slot.depositField
                                  ? original[slot.depositField]
                                  : undefined
                          ),
                alwaysShow: index === 0 || (index < 3 && !isCompleted),
            }));

            paymentRows.forEach(({ label, amount, alwaysShow }, index) => {
                if (amount === null && !alwaysShow) return;
                summaryRows.push({
                    label,
                    amount,
                    gapBefore: index === 0 ? 0.35 : 0,
                });
            });

            const firstPaymentAmount =
                toAmount(original.firstPayment) || depositAmount;
            const laterPaymentsAmount = paymentRows
                .slice(1)
                .reduce((sum, row) => sum + (row.amount || 0), 0);
            const balance =
                Math.round(
                    (grandTotal - firstPaymentAmount - laterPaymentsAmount) * 100
                ) / 100;

            if (balance > 0) {
                summaryRows.push({ label: "BALANCE:", amount: balance });
            }
        }

        const summaryTopGap = 2;
        const summaryBlockHeight =
            summaryTopGap +
            summaryRows.reduce(
                (height, row) => height + summaryRowStep + (row.gapBefore || 0),
                0
            ) +
            1;
        if (needsNewPage(summaryBlockHeight)) {
            createNewPage();
        }

        const summaryTopY = currentY + summaryTopGap;
        currentPage.drawLine({
            start: { x: 30, y: pageHeight - summaryTopY * fontSize },
            end: { x: 560, y: pageHeight - summaryTopY * fontSize },
            color: rgb(0, 0, 0),
            opacity: 0.75,
        });

        let totalY = summaryTopY + 0.3;
        summaryRows.forEach(({ label, amount, gapBefore = 0 }) => {
            totalY += summaryRowStep + gapBefore;
            const rowY = pageHeight - totalY * fontSize;
            const amountText =
                amount === null ? "-" : NumberFormatter.format(amount);

            [
                [label, summaryLabelX],
                [currencySymbol, summaryCurrencyX],
                [amountText, getAmountTextX(amountText)],
            ].forEach(([text, x]) => {
                currentPage.drawText(text, {
                    x,
                    y: rowY,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });
            });
        });

        const summaryBottomY = totalY + 0.7;
        currentPage.drawLine({
            start: { x: 30, y: pageHeight - summaryBottomY * fontSize },
            end: { x: 560, y: pageHeight - summaryBottomY * fontSize },
            color: rgb(0, 0, 0),
            opacity: 0.75,
        });

        // Crop page to remove blank space when invoice is short (single page only)
        // Content is drawn from top (y=pageHeight) down; blank space is at bottom (y=0)
        if (!hasMultiplePages) {
            const contentHeight = summaryBottomY * fontSize + bottomMargin;
            const minPageHeight = 400;
            const newPageHeight = Math.min(
                defaultPageHeight,
                Math.max(minPageHeight, contentHeight)
            );
            if (newPageHeight < defaultPageHeight) {
                // Crop bottom blank space: set media box to show only top portion
                const cropY = defaultPageHeight - newPageHeight;
                currentPage.setMediaBox(0, cropY, defaultPageWidth, newPageHeight);
            }
        }
        }

        // Serialize the PDFDocument to bytes (a Uint8Array)
        const pdfBytes = await pdfDoc.save();

        // Trigger the browser to download the PDF document
        download(
            pdfBytes,
            `${isDeliveryOrder ? "Delivery_Order" : original.invoiceId}-${original?.userId?.name}`,
            "application/pdf"
        );
    } catch (err) {
        alert("Failed");
        console.log(err);
    }
};

export default createCustomerInvoice;

export const createCustomerInvoiceMerged = (original) =>
    createCustomerInvoice(original, { mergeLabourIntoLastItem: true });

export const createDeliveryOrder = (original) =>
    createCustomerInvoice(original, {
        mergeLabourIntoLastItem: true,
        isDeliveryOrder: true,
    });

